import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Investigation, ManagerOutcome } from '../lib/types'
import { investigationsApi } from '../api/investigations'
import { ApiError } from '../lib/api'
import { investigationStatusLabel, MANAGER_OUTCOME_LABEL } from '../lib/format'
import { DerivedActionChecklist } from './DerivedActionChecklist'
import { ExportPdfButton } from './ExportPdfButton'
import { Spinner } from './Spinner'
import { PublishModal } from './portal/PublishModal'
import { portalAdminApi } from '../api/portalAdmin'

export function InvestigationCard({
  investigation,
  reportId,
}: {
  investigation: Investigation
  reportId: string
}) {
  const isAi = investigation.type === 'ai_auto'

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
            isAi ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
          }`}
        >
          {isAi ? '🤖 IA' : '👤 Gerente'}
        </span>
        <span
          className={`text-xs font-medium ${investigation.status === 'ai_error' ? 'text-red-600' : 'text-slate-500'}`}
        >
          {investigationStatusLabel(investigation.status)}
        </span>
      </div>

      <h3 className="font-semibold text-slate-900">{investigation.title}</h3>
      <p className="mt-1 text-sm text-slate-500">
        <span className="font-medium">{isAi ? 'Hipótese' : 'O que investigar'}: </span>
        {investigation.hypothesis}
      </p>

      {isAi ? (
        <AiBody investigation={investigation} reportId={reportId} />
      ) : (
        <ManagerBody investigation={investigation} reportId={reportId} />
      )}
      {(investigation.aiFindings || investigation.managerFindings) && <PublishFinding investigation={investigation} />}
    </div>
  )
}

/** Achado para o portal do cliente: a IA escreve o rascunho em linguagem de cliente, o gerente revisa e publica. */
function PublishFinding({ investigation }: { investigation: Investigation }) {
  const [open, setOpen] = useState(false)
  const draft = useMutation({ mutationFn: () => portalAdminApi.findingDraft(investigation.id) })
  const publish = useMutation({
    mutationFn: (a: { title: string; content: string }) => portalAdminApi.publishFinding(investigation.id, a.title, a.content),
  })
  return (
    <div className="mt-3 flex justify-end border-t border-slate-100 pt-2">
      <button
        type="button"
        onClick={() => {
          publish.reset()
          setOpen(true)
          if (!draft.data) draft.mutate()
        }}
        className="text-xs font-medium text-brand-700 hover:underline"
      >
        Publicar para o cliente
      </button>
      <PublishModal
        open={open}
        heading="Publicar o achado no portal do cliente"
        initialTitle={draft.data?.title ?? investigation.title}
        initialContent={draft.data?.content ?? ''}
        loadingDraft={draft.isPending}
        publishing={publish.isPending}
        error={
          draft.error instanceof Error
            ? `Não foi possível escrever o rascunho: ${draft.error.message}`
            : publish.error instanceof Error
              ? publish.error.message
              : null
        }
        done={publish.isSuccess}
        onClose={() => setOpen(false)}
        onPublish={(title, content) => publish.mutate({ title, content })}
      />
    </div>
  )
}

function AiBody({ investigation, reportId }: { investigation: Investigation; reportId: string }) {
  const findingsRef = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()
  const retry = useMutation({
    mutationFn: () => investigationsApi.retry(investigation.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', reportId] }),
  })

  if (investigation.status === 'ai_running') {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
        <Spinner className="h-4 w-4" />
        Investigando…
      </div>
    )
  }

  // Falhou (antes ficava "Investigando…" para sempre): mostra o motivo e permite refazer.
  if (investigation.status === 'ai_error') {
    return (
      <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        <p className="font-semibold">A investigação automática não terminou.</p>
        {investigation.errorMessage && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-red-700">{investigation.errorMessage}</p>
        )}
        <button
          type="button"
          onClick={() => retry.mutate()}
          disabled={retry.isPending}
          className="mt-2 inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-100 disabled:opacity-50"
        >
          {retry.isPending && <Spinner className="h-3.5 w-3.5" />}
          Refazer a investigação
        </button>
        {retry.isError && (
          <p className="mt-1 text-xs">{retry.error instanceof ApiError ? retry.error.message : 'Erro ao refazer.'}</p>
        )}
      </div>
    )
  }

  if (!investigation.aiFindings) return null

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Achados da IA
        </h4>
        <ExportPdfButton
          targetRef={findingsRef}
          title={`Investigação (IA) — ${investigation.title}`}
          subtitle={investigation.hypothesis ? `Hipótese: ${investigation.hypothesis}` : undefined}
          label="Exportar para PDF"
          className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 print:hidden"
        />
      </div>
      <div ref={findingsRef} className="markdown text-sm">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{investigation.aiFindings}</ReactMarkdown>
      </div>
      <DerivedActionChecklist actions={investigation.derivedActions} reportId={reportId} />
    </div>
  )
}

const OUTCOMES: ManagerOutcome[] = ['confirmed', 'refuted', 'unverified']
const OUTCOME_BTN: Record<ManagerOutcome, string> = {
  confirmed: '✓ Confirmei',
  refuted: '✗ Descartei',
  unverified: '? Não consegui verificar',
}

/**
 * Investigação para o gerente: resposta em 1 clique (confirmei / descartei / não deu
 * para verificar) + comentário opcional. Antes o texto livre era obrigatório — e os
 * achados zeraram.
 */
export function ManagerOutcomeForm({
  investigationId,
  onDone,
}: {
  investigationId: string
  onDone: () => void
}) {
  const [note, setNote] = useState('')
  const [showNote, setShowNote] = useState(false)
  const mutation = useMutation({
    mutationFn: (outcome: ManagerOutcome) => investigationsApi.submitFindings(investigationId, outcome, note),
    onSuccess: onDone,
  })
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <p className="mb-1.5 text-sm font-semibold text-slate-700">O que você encontrou?</p>
      <div className="flex flex-wrap items-center gap-1.5">
        {OUTCOMES.map((o) => (
          <button
            key={o}
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(o)}
            className="rounded-full border border-brand-300 bg-white px-3 py-1 text-xs font-semibold text-brand-700 transition hover:bg-brand-600 hover:text-white disabled:opacity-50"
          >
            {OUTCOME_BTN[o]}
          </button>
        ))}
        {mutation.isPending && <Spinner className="h-4 w-4" />}
        <button
          type="button"
          onClick={() => setShowNote((v) => !v)}
          className="ml-auto text-xs text-slate-500 hover:underline"
        >
          {showNote ? 'sem comentário' : '+ comentário (opcional)'}
        </button>
      </div>
      {showNote && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="O que você viu na conta (vai junto com a resposta)…"
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        />
      )}
      {mutation.isError && (
        <p className="mt-1 text-xs text-red-600">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Erro ao enviar.'}
        </p>
      )}
    </div>
  )
}

function ManagerBody({
  investigation,
  reportId,
}: {
  investigation: Investigation
  reportId: string
}) {
  const qc = useQueryClient()

  // Concluída — mostra o resultado + comentário + ações derivadas
  if (investigation.status === 'manager_complete' || investigation.managerFindings) {
    return (
      <div className="mt-3 border-t border-slate-100 pt-3">
        <h4 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {investigation.managerOutcome ? MANAGER_OUTCOME_LABEL[investigation.managerOutcome] : 'Seus achados'}
        </h4>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{investigation.managerFindings}</p>
        <DerivedActionChecklist actions={investigation.derivedActions} reportId={reportId} />
      </div>
    )
  }

  return (
    <ManagerOutcomeForm
      investigationId={investigation.id}
      onDone={() => qc.invalidateQueries({ queryKey: ['report', reportId] })}
    />
  )
}
